import React, { useState } from 'react';
import { UserPlus, Shield, ArrowLeft, Sparkles, Mail, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { validateRegisterForm, getPasswordStrength } from '../utils/validation';
import { sendVerificationCode, verifyCode } from '../utils/emailVerification';
interface PatientSignupPageProps {
  onBackToLogin: () => void;
}
export function PatientSignupPage({
  onBackToLogin
}: PatientSignupPageProps) {
  const {
    register
  } = useAuth();
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const passwordStrength = password ? getPasswordStrength(password) : null;
  const strengthColors = {
    weak: 'bg-red-400',
    medium: 'bg-amber-400',
    strong: 'bg-emerald-400',
    very_strong: 'bg-emerald-500'
  };
  const handleSendCode = async () => {
    setRegisterError('');
    setVerificationError('');
    const validation = validateRegisterForm(name, email, password, confirmPassword);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});
    setIsSendingCode(true);
    const result = await sendVerificationCode(email);
    if (result.success) {
      setStep('verify');
    } else {
      setRegisterError(result.error || 'Error al enviar código de verificación');
    }
    setIsSendingCode(false);
  };
  const handleVerifyCode = async () => {
    setVerificationError('');
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Por favor ingresa el código de 6 dígitos');
      return;
    }
    setIsVerifying(true);
    const result = await verifyCode(email, verificationCode);
    if (result.success) {
      await handleRegister();
    } else {
      setVerificationError(result.error || 'Código incorrecto');
    }
    setIsVerifying(false);
  };
  const handleRegister = async () => {
    setIsLoading(true);
    const result = await register(name, email, password, 'paciente');
    if (!result.success) {
      setRegisterError(result.error || 'Error al crear la cuenta');
      setStep('form');
    }
    setIsLoading(false);
  };
  const handleResendCode = async () => {
    setVerificationError('');
    setIsSendingCode(true);
    const result = await sendVerificationCode(email);
    if (result.success) {
      setVerificationError('');
      setVerificationCode('');
    } else {
      setVerificationError(result.error || 'Error al reenviar código');
    }
    setIsSendingCode(false);
  };
  // Verification Step
  if (step === 'verify') {
    return <div className="min-h-screen bg-gradient-to-br from-cyan-50/50 via-white to-teal-50/30 flex items-center justify-center p-4" data-id="element-6750" data-mp="ds9b9i0e9qxm">
        <div className="w-full max-w-md" data-id="element-6751" data-mp="3d8ny6oj9ibpy">
          <button onClick={() => setStep('form')} className="mb-8 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors group" data-id="element-6752" data-mp="3i5xyiz86w7sk">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-6753" data-mp="3m685of7px490" />
            <span className="text-sm font-medium" data-id="element-6754" data-mp="3qzaxahsxq3mk">Volver</span>
          </button>

          <div className="bg-white rounded-3xl shadow-lg shadow-teal-100/50 p-8 sm:p-10 border border-gray-100" data-id="element-6755" data-mp="2ufk4x49yi0vv">
            <div className="text-center mb-8" data-id="element-6756" data-mp="2ygvvbc0nmq54">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-lg shadow-teal-200/50 mb-6" data-id="element-6757" data-mp="2fdlfciamy0j0">
                <Mail className="w-8 h-8 text-white" strokeWidth={2.5} data-id="element-6758" data-mp="2agbp50ff9ku4" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6759" data-mp="2p4przbcu99ao">
                Verifica tu correo
              </h2>
              <p className="text-sm text-gray-600 mb-1" data-id="element-6760" data-mp="2katxiivsa4k9">Código enviado a</p>
              <p className="text-sm font-semibold text-teal-600" data-id="element-6761" data-mp="2y28iuwd5yhuh">{email}</p>
            </div>

            {verificationError && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl" data-id="element-6762" data-mp="2t7cp9rsa3mf9">
                <p className="text-sm text-red-700 font-medium" data-id="element-6763" data-mp="381hio5upmqor">
                  {verificationError}
                </p>
              </div>}

            <div className="space-y-6" data-id="element-6764" data-mp="1dnq20mdyr6jt">
              <div data-id="element-6765" data-mp="1dorpjqc61lif">
                <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-6766" data-mp="1dqcaco8ohq33">
                  Código de Verificación
                </label>
                <input type="text" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-teal-100 focus:border-teal-500 transition-all outline-none" placeholder="000000" autoFocus data-id="element-6767" data-mp="3kc8i3s3pb5k0" />
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-6768" data-mp="31m0oc0lw5rgh">
                  El código expira en 10 minutos
                </p>
              </div>

              <button onClick={handleVerifyCode} disabled={isVerifying || verificationCode.length !== 6} className="w-full h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-200/50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6769" data-mp="35ox8vwuzrxn2">
                {isLoading ? 'Creando cuenta...' : isVerifying ? 'Verificando...' : 'Verificar y Crear Cuenta'}
              </button>

              <div className="text-center pt-2" data-id="element-6770" data-mp="tfyneow36w64">
                <p className="text-sm text-gray-600 mb-2" data-id="element-6771" data-mp="oeriqyp8qd3o">
                  ¿No recibiste el código?
                </p>
                <button onClick={handleResendCode} disabled={isSendingCode} className="text-sm text-teal-600 font-semibold hover:text-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-6772" data-mp="jh97h1nr3lsi">
                  {isSendingCode ? 'Reenviando...' : 'Reenviar código'}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-6773" data-mp="12f2980mn1pob">
            <Shield className="w-4 h-4 text-teal-600" data-id="element-6774" data-mp="xfz46g6nrjgc" />
            <span data-id="element-6775" data-mp="xf7ka2mfba5w">Verificación segura de correo electrónico</span>
          </div>
        </div>
      </div>;
  }
  // Registration Form Step
  return <div className="min-h-screen bg-gradient-to-br from-cyan-50/50 via-white to-teal-50/30 flex items-center justify-center p-4" data-id="element-6776" data-mp="1ux7j9dabclxf">
      <div className="w-full max-w-md" data-id="element-6777" data-mp="1ye2ciu6jbyyw">
        <button onClick={onBackToLogin} className="mb-8 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors group" data-id="element-6778" data-mp="1yf3p53378tiq">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-6779" data-mp="27e8kk0jgs6qo" />
          <span className="text-sm font-medium" data-id="element-6780" data-mp="2cd21y41vghw4">
            Volver al inicio de sesión
          </span>
        </button>

        <div className="text-center mb-8" data-id="element-6781" data-mp="2hcy6c4ehebc7">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-lg shadow-teal-200/50 mb-6" data-id="element-6782" data-mp="2hbwj71k6xye4">
            <UserPlus className="w-8 h-8 text-white" strokeWidth={2.5} data-id="element-6783" data-mp="2q5hyqn46jjol" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6784" data-mp="2v3t86wqkbs6a">
            Crear Cuenta de Paciente
          </h1>
          <div className="flex items-center justify-center gap-2 text-teal-600" data-id="element-6785" data-mp="303xoalnk6kvz">
            
            <p className="text-sm font-medium" data-id="element-6786" data-mp="352sew9rrpdqn">Sistema Savidna</p>
            
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg shadow-teal-100/50 p-8 sm:p-10 border border-gray-100" data-id="element-6787" data-mp="3qbcu38plnjn7">
          <div className="mb-8" data-id="element-6788" data-mp="3ldbdqvjk5yff">
            <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-6789" data-mp="37zp502yriuu">Bienvenido</h2>
            <p className="text-sm text-gray-600" data-id="element-6790" data-mp="35vn6nomktx7">
              Crea tu cuenta para registrar tu información médica de forma
              segura
            </p>
          </div>

          {registerError && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl" data-id="element-6791" data-mp="36mm07mimk78e">
              <p className="text-sm text-red-700 font-medium" data-id="element-6792" data-mp="32oo748lqh0zq">
                {registerError}
              </p>
            </div>}

          <form onSubmit={e => {
          e.preventDefault();
          handleSendCode();
        }} className="space-y-5" data-id="element-6793" data-mp="3ggcig9o5rlfn">
            <div data-id="element-6794" data-mp="2nxp2f8o0qwlf">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6795" data-mp="2nx5o01xkuqc5">
                Nombre Completo
              </label>
              <input type="text" placeholder="Juan Pérez" value={name} onChange={e => setName(e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.name ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-6796" data-mp="2ix1gh1end5y2" />
              {errors.name && <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1" data-id="element-6797" data-mp="ugjem8f4o4ij">
                  {errors.name}
                </p>}
            </div>

            <div data-id="element-6798" data-mp="1i37yv0rm092b">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6799" data-mp="1m6e88ha0qgd0">
                Correo Electrónico
              </label>
              <input type="email" placeholder="correo@ejemplo.com" value={email} onChange={e => setEmail(e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.email ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-6800" data-mp="1863dh35l8hmk" />
              {errors.email && <p className="text-xs text-red-600 mt-1.5" data-id="element-6801" data-mp="3e9plql4ensur">{errors.email}</p>}
            </div>

            <div data-id="element-6802" data-mp="3j9kc8nk9qgfa">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6803" data-mp="3j80wqz6fs9ek">
                Contraseña
              </label>
              <div className="relative" data-id="element-6804" data-mp="34jwefwbapzwb">
                <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className={`w-full px-4 py-3 pr-11 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.password ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-6805" data-mp="39ippzj70vgtu" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-6806" data-mp="2qk517xslsnjd">
                  {showPassword ? <EyeOff className="w-5 h-5" data-id="element-6807" data-mp="otjsako5afe4" /> : <Eye className="w-5 h-5" data-id="element-6808" data-mp="osi89qp5iik9" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1.5" data-id="element-6809" data-mp="29bc9r147kz0i">{errors.password}</p>}
              {password && passwordStrength && <div className="mt-3" data-id="element-6810" data-mp="249vb7c975on3">
                  <div className="flex items-center gap-2 mb-1" data-id="element-6811" data-mp="2j1e05zk1atil">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-6812" data-mp="2j2p2duxtzt7l">
                      <div className={`h-full rounded-full transition-all duration-500 ${strengthColors[passwordStrength.level]}`} style={{
                    width: `${Math.min(100, passwordStrength.score / 7 * 100)}%`
                  }} data-id="element-6813" data-mp="2e82p1m86vqkv" />
                    </div>
                    <span className={`text-xs font-semibold ${passwordStrength.level === 'weak' ? 'text-red-500' : passwordStrength.level === 'medium' ? 'text-amber-500' : 'text-emerald-500'}`} data-id="element-6814" data-mp="31yf7hr1295pp">
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-6815" data-mp="12nxjq9qhm0bh">
                    Mín. 8 caracteres, mayúscula, minúscula, número y símbolo
                  </p>
                </div>}
            </div>

            <div data-id="element-6816" data-mp="1wdedyhpyohio">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6817" data-mp="1wcv7bx5rhfjy">
                Confirmar Contraseña
              </label>
              <div className="relative" data-id="element-6818" data-mp="26aj5kx2kt56v">
                <input type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full px-4 py-3 pr-11 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.confirmPassword ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-6819" data-mp="26bkjd9az85sa" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-6820" data-mp="2p08yfems3gw5">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" data-id="element-6821" data-mp="nbv0029oqinr" /> : <Eye className="w-5 h-5" data-id="element-6822" data-mp="nc4o1kb66fqt" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-600 mt-1.5" data-id="element-6823" data-mp="ibyws58tav99">
                  {errors.confirmPassword}
                </p>}
            </div>

            <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-teal-50/80 to-cyan-50/50 border border-teal-100 rounded-2xl" data-id="element-6824" data-mp="dfi5512elyrf">
              <Shield className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-6825" data-mp="162m7sze0y101" />
              <div data-id="element-6826" data-mp="113j6nq160exc">
                <p className="text-xs text-teal-900 font-semibold mb-0.5" data-id="element-6827" data-mp="114auufsfnadl">
                  Información Segura y Confidencial
                </p>
                <p className="text-xs text-teal-700 leading-relaxed" data-id="element-6828" data-mp="wagglrvljnlw">
                  Tu contraseña se almacena de forma segura con cifrado SHA-256.
                  Toda tu información médica está protegida.
                </p>
              </div>
            </div>

            <button type="submit" disabled={isSendingCode} className="w-full h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-200/50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6829" data-mp="1k1c44ct0rwnc">
              {isSendingCode ? 'Enviando código...' : 'Continuar'}
            </button>
          </form>

          <div className="mt-6 text-center" data-id="element-6830" data-mp="zn3t15h8phgw">
            <p className="text-sm text-gray-600" data-id="element-6831" data-mp="1na29w499urfc">
              ¿Ya tienes una cuenta?{' '}
              <button onClick={onBackToLogin} className="text-teal-600 font-semibold hover:text-teal-700 transition-colors" data-id="element-6832" data-mp="1sa83vr30kq0l">
                Iniciar sesión
              </button>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6 leading-relaxed" data-id="element-6833" data-mp="1iitzb4w7h00l">
          Al crear una cuenta, recibirás un código de verificación en tu correo
          para confirmar tu identidad.
        </p>
      </div>
    </div>;
}